import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import participantPageQuery from "../../participants/participant.graphql";
import OpenGraphMeta from "@default/js/general-components/seo/open-graph-meta";
import { stripHtml } from "@default/js/general-components/seo/utils";
import { self } from "@default/js/config";
import LookingFor from "../../participants/components/looking-for";
import SocialLinks from "../../participants/components/social-links";
import Sponsors from "../../participants/components/sponsors";
import Appearances from "../shared-components/appearances/appearances";
import NodeWrapper from "@default/nodes/shared-components/node-wrapper";
import useNodeQuery from "@default/hooks/use-node-query";
import SpotifyWidget from "@default/js/participants/components/spotify-widget/spotify-widget";
import DetailPageIntro from "@default/nodes/shared-components/detail-page-intro";
import { useSelector } from "react-redux";
import ParagraphClickSuggestions from "@default/paragraphs/click-suggestions/paragraph-click-suggestions";
import CheckForChildren from "@default/lib/check-for-children";
import Credit from "./../shared-components/credit";
import classNames from "classnames";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import ArchivedEventInfo from "@default/nodes/shared-components/archived-event-info";
import filterAppearancesByEdition from "@default/lib/filter-appearances-by-edition";

const Speaker = () => {
  const mainSettings = useSelector(
      (reduxStore) => reduxStore.appStore.mainSettings
    ),
    customHistory = useSelector(
      (reduxStore) => reduxStore.appStore.customHistory
    );

  const { simpleDesign } = useAccessibilityContext();

  const [backToOverviewContent, setBackToOverviewContent] = useState({
    fieldClickSuggestions: [],
  });

  const { entity, loading, error } = useNodeQuery(
    participantPageQuery,
    undefined
  );
  const { pathname } = useLocation();
  const currentEditionEntity = entity
    ? {
        ...entity,
        fieldAppearances: filterAppearancesByEdition(
          entity.fieldAppearances,
          entity.fieldEdition?.id
        ),
      }
    : null;

  useEffect(() => {
    if (entity) {
      prepareBackToOverviewContent(entity);
    }
  }, [entity]);

  /**
   * If the node is loaded, check if speaker is on Festival, Conference or both branches.
   * Get the corresponding "overview Link" and restructure the content to have them displayed by the existing module.
   * @param {String} navigateBack is used when the last visited URL is the same as the given Overview Page, to ensure we use the right Scrollposition
   */

  const prepareBackToOverviewContent = (node) => {
    let prepareBackToOverviewContent = {
      fieldClickSuggestions: [],
    };

    if (
      !!mainSettings?.fieldSpeakersFestivalOverview &&
      node?.fieldBranch.includes("festival")
    ) {
      let festivalOverviewLink = {
        fieldHighlighted: true,
        fieldLink: mainSettings?.fieldSpeakersFestivalOverview,
        fieldImage: mainSettings?.fieldFestivalSpeakersOwImage,
        navigateBack:
          mainSettings?.fieldSpeakersFestivalOverview?.uri?.path ===
          customHistory[customHistory.length - 2]?.pathname,
      };

      prepareBackToOverviewContent.fieldClickSuggestions.push(
        festivalOverviewLink
      );
    }

    if (
      !!mainSettings?.fieldSpeakersConfOverview &&
      node?.fieldBranch.includes("conference")
    ) {
      let conferenceOverviewLink = {
        fieldHighlighted: true,
        fieldLink: mainSettings?.fieldSpeakersConfOverview,
        fieldImage: mainSettings?.fieldConfSpeakersOwImage,
        navigateBack:
          mainSettings?.fieldSpeakersConfOverview?.uri?.path ===
          customHistory[customHistory.length - 2]?.pathname,
      };

      prepareBackToOverviewContent.fieldClickSuggestions.push(
        conferenceOverviewLink
      );
    }

    if (prepareBackToOverviewContent !== backToOverviewContent) {
      setBackToOverviewContent(prepareBackToOverviewContent);
    }
  };

  return (
    <NodeWrapper entity={entity} loading={loading} error={error}>
      {entity && (
        <>
          <OpenGraphMeta
            title={entity.title || entity.label}
            type="profile"
            url={self + pathname}
            description={stripHtml(entity.fieldBiography || entity.fieldText)}
            image={
              entity.fieldImage?.fieldMediaImage?.style?.url
                ? {
                    url: entity.fieldImage.fieldMediaImage.style.url,
                    alt: entity.fieldImage.fieldMediaImage.alt,
                  }
                : undefined
            }
            profileMeta={{
              firstName: entity.fieldFirstNameWeb,
              lastName: entity.fieldLastNameWeb,
            }}
          />
          <DetailPageIntro content={entity} mode={"speaker"} />

          <div
            className={classNames({
              "main-infos speaker": true,
              container: simpleDesign,
              "container-fluid": !simpleDesign,
            })}
          >
            <div className="row">
              <div
                className={classNames({
                  "col-12 meta-info-wrapper": true,
                  "col-xl-3": !simpleDesign,
                })}
              >
                {entity.fieldCompany && (
                  <div
                    className={"text body-m"}
                    dangerouslySetInnerHTML={{ __html: entity.fieldCompany }}
                  />
                )}
                {entity.fieldPosition && (
                  <div
                    className={"text body-m"}
                    dangerouslySetInnerHTML={{ __html: entity.fieldPosition }}
                  />
                )}
                {!!entity.fieldCountry?.list?.length && (
                  <>
                    {entity.fieldCountry.list.map((country) => (
                      <div
                        className="tag black body-s d-inline-flex"
                        key={country}
                      >
                        {country.value}
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div
                className={classNames({
                  "col-12 appearances-wrapper": true,
                  "col-xl-9": !simpleDesign,
                })}
              >
                <Appearances
                  detailedMode={true}
                  content={currentEditionEntity}
                  type={"speaker"}
                  viewMode={"detail"}
                  showAddToCalendar={true}
                  showEndDate={true}
                />
              </div>
            </div>
          </div>

          <div className="container max-width main-container">
            <div className="row text-wrapper">
              <ArchivedEventInfo content={currentEditionEntity} />

              {entity.fieldBiography && (
                <div className="col-12 text-wrapper">
                  <div
                    className={"text headline-subhead"}
                    dangerouslySetInnerHTML={{ __html: entity.fieldBiography }}
                  />
                </div>
              )}

              <Credit content={entity} />
            </div>
            <div className="row with-gaps">
              <CheckForChildren classes={"col-12 col-lg-6 left-col"}>
                <SpotifyWidget content={entity} />

                <LookingFor content={entity} />

                <Sponsors content={currentEditionEntity} />
              </CheckForChildren>

              <CheckForChildren classes={"col-12 col-lg-6 right-col"}>
                <SocialLinks content={entity} />
              </CheckForChildren>

              {/* Checks if the user comes from Speaker Overview to force rhe user to use History instead of Link. */}
              {backToOverviewContent && (
                <div className="back-to-overview">
                  <ParagraphClickSuggestions
                    content={backToOverviewContent}
                    forceSameTab={true}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </NodeWrapper>
  );
};

Speaker.propTypes = {};

export default Speaker;
